import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Card, CardContent, TextField, Button, Typography, Alert, CircularProgress, Link as MuiLink } from '@mui/material';
import { api } from '../services/api';
import { useStore } from '../store/useStore';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = useStore(s => s.setAuth);
  const [mode, setMode] = useState<'password' | 'pin'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const theme = useTheme();

  useEffect(() => {
    if (location.state?.message) {
      setSuccess(location.state.message);
    }
  }, [location.state]);

  const parseError = (err: any) => {
    const detail = err?.response?.data?.detail;
    if (Array.isArray(detail)) return detail.map((d: any) => d.msg).join(', ');
    if (typeof detail === 'string') return detail;
    return err?.message || 'Login failed';
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Email is required.');
      return;
    }
    if (mode === 'password' && !password.trim()) {
      setError('Password is required.');
      return;
    }
    if (mode === 'pin' && !/^\d{4,6}$/.test(pin)) {
      setError('PIN must be 4–6 digits.');
      return;
    }

    setLoading(true); setError(''); setSuccess('');
    try {
      const { access_token, user } = mode === 'password'
        ? await api.login(email, password)
        : await api.loginWithPin(email, pin);
      setAuth(user, access_token);
      navigate('/dashboard');
    } catch (err: any) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(m => m === 'password' ? 'pin' : 'password');
    setError(''); setPassword(''); setPin('');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Card sx={{ width: { xs: '92%', sm: 420 }, bgcolor: 'background.paper', borderRadius: 3, border: `1px solid ${alpha(theme.palette.text.primary, 0.06)}` }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: '700', textAlign: 'center', mb: 0.5 }}>
            Surface IQ
          </Typography>
          <Typography sx={{ color: 'text.secondary', textAlign: 'center', mb: 4, fontSize: 14 }}>
            Construction Inspector Platform
          </Typography>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box component="form" onSubmit={handleLogin}>
            <TextField
              fullWidth label="Email" type="email" value={email}
              onChange={e => setEmail(e.target.value)}
              sx={{ mb: 2 }}
            />
            {mode === 'password' ? (
              <TextField
                fullWidth label="Password" type="password" value={password}
                onChange={e => setPassword(e.target.value)}
                sx={{ mb: 2 }}
              />
            ) : (
              <TextField
                fullWidth label="PIN (4–6 digits)" type="password" value={pin}
                onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6, autoComplete: 'one-time-code' }}
                sx={{ mb: 2 }}
              />
            )}
            <Box sx={{ textAlign: 'right', mb: 2 }}>
              <MuiLink component="button" type="button" onClick={toggleMode}
                sx={{ color: 'primary.main', fontSize: 13, textDecoration: 'none' }}>
                {mode === 'password' ? 'Sign in with PIN' : 'Sign in with password'}
              </MuiLink>
            </Box>
            <Button
              type="submit" fullWidth variant="contained" disabled={loading}
              color="primary" sx={{ py: 1.5, fontWeight: 700, fontSize: 15 }}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'LOGIN'}
            </Button>
          </Box>
          <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontSize: 14, mt: 2 }}>
            Don't have an account?{' '}
            <MuiLink component={Link} to="/register" sx={{ color: 'primary.main', textDecoration: 'none' }}>
              Register
            </MuiLink>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
